/**
 * RoadMonks Theme JS
 * Handles: carousels, search overlay, scroll animations, product gallery, mute toggle, mobile nav
 */

(function () {
  'use strict';

  /* ========================================================
     SCROLL REVEAL ANIMATIONS
  ======================================================== */
  function initReveal() {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          setTimeout(() => entry.target.classList.add('visible'), i * 80);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    reveals.forEach(el => observer.observe(el));
  }

  /* ========================================================
     GENERIC SLIDER FACTORY
     Usage: initSlider({ trackId, prevBtn, nextBtn, getItemsVisible })
  ======================================================== */
  function initSlider({ trackId, prevSelector, nextSelector, getItemsVisible }) {
    const track = document.getElementById(trackId);
    if (!track) return;

    const prevBtn = document.querySelector(prevSelector);
    const nextBtn = document.querySelector(nextSelector);
    let currentIndex = 0;

    function getCards() { return track.children; }

    function getVisible() {
      if (typeof getItemsVisible === 'function') return getItemsVisible();
      const w = window.innerWidth;
      if (w >= 1200) return 4;
      if (w >= 992) return 3;
      if (w >= 576) return 2;
      return 1;
    }

    function slide() {
      const cards = getCards();
      if (!cards.length) return;
      const cardWidth = cards[0].offsetWidth + parseInt(getComputedStyle(track).gap || '0');
      track.style.transform = `translateX(-${currentIndex * cardWidth}px)`;
    }

    function next() {
      const cards = getCards();
      const visible = getVisible();
      const max = Math.max(0, cards.length - visible);
      currentIndex = currentIndex >= max ? 0 : currentIndex + 1;
      slide();
    }

    function prev() {
      const cards = getCards();
      const visible = getVisible();
      const max = Math.max(0, cards.length - visible);
      currentIndex = currentIndex <= 0 ? max : currentIndex - 1;
      slide();
    }

    nextBtn?.addEventListener('click', next);
    prevBtn?.addEventListener('click', prev);
    window.addEventListener('resize', slide);

    // Touch / swipe support
    let touchStartX = 0;
    track.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
    track.addEventListener('touchend', e => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
    }, { passive: true });
  }

  /* ========================================================
     TESTIMONIALS SLIDER
  ======================================================== */
  function initTestimonialsSlider() {
    initSlider({
      trackId: 'testimonials-grid',
      prevSelector: '.testimonial-prev',
      nextSelector: '.testimonial-next',
      getItemsVisible() {
        const w = window.innerWidth;
        if (w >= 992) return 3;
        if (w >= 576) return 2;
        return 1;
      }
    });
  }

  /* ========================================================
     BIKE BRANDS SLIDER
  ======================================================== */
  function initBikesSlider() {
    initSlider({
      trackId: 'bike-track',
      prevSelector: '.bike-prev',
      nextSelector: '.bike-next',
      getItemsVisible() {
        const w = window.innerWidth;
        if (w >= 1200) return 4;
        if (w >= 992) return 3;
        if (w >= 576) return 2;
        return 1;
      }
    });
  }

  /* ========================================================
     SEARCH OVERLAY
  ======================================================== */
  function initSearch() {
    const overlay = document.getElementById('search-overlay');
    const triggers = document.querySelectorAll('#search-trigger, .search-trigger-btn');
    const closeBtn = document.getElementById('search-overlay-close');
    const input = overlay?.querySelector('.search-overlay-input');

    if (!overlay || triggers.length === 0) return;

    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        overlay.classList.add('is-open');
        overlay.setAttribute('aria-hidden', 'false');
        setTimeout(() => input?.focus(), 100);
      });
    });

    closeBtn?.addEventListener('click', closeSearch);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeSearch();
    });

    function closeSearch() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
    }
  }

  /* ========================================================
     PRODUCT IMAGE GALLERY (product page)
  ======================================================== */
  function initProductGallery() {
    const featuredImg = document.getElementById('product-featured-img');
    const thumbBtns = document.querySelectorAll('.product-thumb-btn');

    if (!featuredImg || !thumbBtns.length) return;

    thumbBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const newSrc = btn.dataset.imgSrc;
        if (!newSrc) return;
        featuredImg.src = newSrc;
        thumbBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });
  }

  /* ========================================================
     PRODUCT VARIANT SELECTOR

     Matches Shopify's native option1/option2/option3 model (each radio
     carries a data-option-position matching its index in product.options,
     set server-side in main-product.liquid) against the full variant list
     embedded via {{ product.variants | json }}. On match, updates the
     hidden "id" input the (AJAX) add-to-cart form actually submits, plus
     the featured image, price, availability and URL — mirroring Dawn's
     variant-selects behaviour.
  ======================================================== */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const format = window.RoadMonks?.moneyFormat || '₹{{amount}}';
    return format.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(amount));
  }

  function initVariantSelector() {
    const productForm = document.getElementById('product-form');
    if (!productForm) return;

    const variantRadios = productForm.querySelectorAll('[type="radio"]');
    const variantIdInput = productForm.querySelector('#variant-id');
    const variantDataEl = productForm.querySelector('[data-product-variants]');

    if (!variantRadios.length || !variantIdInput || !variantDataEl) return;

    let variants = [];
    try {
      variants = JSON.parse(variantDataEl.textContent);
    } catch (e) {
      return;
    }

    const gallery = document.querySelector('.product-gallery');
    const featuredImg = document.getElementById('product-featured-img');
    const thumbBtns = gallery ? Array.from(gallery.querySelectorAll('.product-thumb-btn')) : [];
    const priceEl = document.getElementById('product-current-price');
    const comparePriceEl = document.getElementById('product-compare-price');
    const savingsBadgeEl = document.getElementById('product-savings-badge');
    const addToCartBtn = document.getElementById('add-to-cart-btn');
    const buyNowBtn = document.getElementById('buy-now-btn');
    const strings = window.RoadMonks?.strings || {};

    function findMatchingVariant() {
      const selections = [];
      variantRadios.forEach(radio => {
        if (!radio.checked) return;
        const position = parseInt(radio.dataset.optionPosition, 10);
        if (position) selections[position - 1] = radio.value;
      });

      return variants.find(variant => (
        (selections[0] === undefined || variant.option1 === selections[0]) &&
        (selections[1] === undefined || variant.option2 === selections[1]) &&
        (selections[2] === undefined || variant.option3 === selections[2])
      ));
    }

    function updateVariant(variant) {
      if (!variant) return;

      // Hidden variant id input — the value cart.js reads at submit time
      variantIdInput.value = variant.id;

      // Reflect the selected variant in the URL without reloading the page
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ variantId: variant.id }, '', url);

      // Featured image — swap to the image assigned to this variant, if any
      if (variant.featured_image && featuredImg) {
        const matchingThumb = thumbBtns.find(btn => parseInt(btn.dataset.mediaId, 10) === variant.featured_image.id);
        if (matchingThumb) {
          featuredImg.src = matchingThumb.dataset.imgSrc;
          thumbBtns.forEach(b => b.classList.remove('active'));
          matchingThumb.classList.add('active');
        }
      }

      // Price
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (comparePriceEl && savingsBadgeEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          comparePriceEl.classList.remove('d-none');
          const savingsPct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          savingsBadgeEl.textContent = `SAVE ${savingsPct}%`;
          savingsBadgeEl.classList.remove('d-none');
        } else {
          comparePriceEl.classList.add('d-none');
          savingsBadgeEl.classList.add('d-none');
        }
      }

      // Availability
      if (addToCartBtn) {
        addToCartBtn.disabled = !variant.available;
        addToCartBtn.innerHTML = variant.available
          ? `<i class="fas fa-shopping-cart me-2"></i>${strings.addToCart || 'Add to Cart'}`
          : (strings.soldOut || 'Sold Out');
      }
      if (buyNowBtn) {
        if (variant.available) {
          buyNowBtn.classList.remove('d-none');
          buyNowBtn.href = buyNowBtn.href.replace(/([?&]id=)\d+/, `$1${variant.id}`);
        } else {
          buyNowBtn.classList.add('d-none');
        }
      }

      document.dispatchEvent(new CustomEvent('rm:variant-changed', { detail: { variant } }));
    }

    variantRadios.forEach(radio => {
      radio.addEventListener('change', () => updateVariant(findMatchingVariant()));
    });
  }

  /* ========================================================
     REEL VIDEOS — MUTE TOGGLE
  ======================================================== */
  function initReels() {
    document.querySelectorAll('[data-reel]').forEach(card => {
      const video = card.querySelector('video');
      const muteBtn = card.querySelector('.mute-toggle');
      const muteIcon = muteBtn?.querySelector('i');

      if (!video || !muteBtn) return;

      muteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        video.muted = !video.muted;
        if (muteIcon) {
          muteIcon.className = video.muted ? 'fas fa-volume-mute' : 'fas fa-volume-up';
        }
      });

      card.addEventListener('click', () => {
        if (video.paused) video.play();
      });
    });
  }

  /* ========================================================
     HEADER SCROLL BEHAVIOUR
     Adds shadow when scrolled
  ======================================================== */
  function initHeaderScroll() {
    const header = document.getElementById('main-header');
    if (!header) return;

    let lastScroll = 0;

    window.addEventListener('scroll', () => {
      const currentScroll = window.pageYOffset;
      if (currentScroll > 10) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }
      lastScroll = currentScroll;
    }, { passive: true });
  }

  /* ========================================================
     MOBILE NAV — handle dropdown toggles
  ======================================================== */
  function initMobileNav() {
    // Dropdown toggles on mobile (for the Bootstrap collapse nav)
    document.querySelectorAll('#navbarNav .nav-link').forEach(link => {
      const parent = link.closest('.nav-item');
      const dropdown = parent?.querySelector('.dropdown');

      if (!dropdown) return;

      link.addEventListener('click', (e) => {
        if (window.innerWidth < 992) {
          e.preventDefault();
          e.stopPropagation();
          parent.classList.toggle('open');
        }
      });
    });
  }

  /* ========================================================
     STICKY PROMO MARQUEE — pause on tab hidden
  ======================================================== */
  function initMarquee() {
    const marquee = document.querySelector('.marquee-content');
    if (!marquee) return;

    document.addEventListener('visibilitychange', () => {
      marquee.style.animationPlayState = document.hidden ? 'paused' : 'running';
    });
  }

  /* ========================================================
     VIEW TOGGLE (grid / list) on collection page
  ======================================================== */
  function initViewToggle() {
    const grid = document.getElementById('collection-product-grid');
    if (!grid) return;

    document.querySelectorAll('.view-icon').forEach(icon => {
      icon.addEventListener('click', () => {
        document.querySelectorAll('.view-icon').forEach(i => i.classList.remove('active'));
        icon.classList.add('active');

        if (icon.dataset.view === 'list') {
          grid.classList.add('list-view');
        } else {
          grid.classList.remove('list-view');
        }
      });
    });
  }

  /* ========================================================
     INIT ALL
  ======================================================== */
  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initBikesSlider();
    initTestimonialsSlider();
    initSearch();
    initProductGallery();
    initVariantSelector();
    initReels();
    initHeaderScroll();
    initMobileNav();
    initMarquee();
    initViewToggle();
  });

})();
